import liff from '@line/liff'
import { useQuery } from '@tanstack/react-query'
import {
  Armchair,
  ChevronLeft,
  Info,
  LogOut,
  Maximize2,
  Minimize2,
  Pencil,
  Search,
  Smartphone,
  Sparkles,
  Users,
  X,
} from 'lucide-react'
import { AnimatePresence, domAnimation, LazyMotion, m } from 'motion/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import logoUrl from '@/assets/logo.webp'
import { LIFFQR } from '@/components/common/LIFFQR'
import { FullPageSpinnerPortal } from '@/components/common/Spinner/FullPagePortal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { getApiGuestsOptions } from '@/lib/api/client/@tanstack/react-query.gen'
import { useLIFF } from '@/lib/liff/context'

import { KioskTablePicker } from './KioskTablePicker'

interface GuestItem {
  id: string
  fullName: string
  displayName?: string | null
  group?: string | null
  side?: 'groom' | 'bride' | null
  status?: string
  tableAssignment?: {
    tableId: string
    tableNumber: string
    zone?: string | null
  } | null
}

export const KioskFindTable = () => {
  const { isLoggedIn, isReady } = useLIFF()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedGuest, setSelectedGuest] = useState<GuestItem | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isFullscreenSupported, setIsFullscreenSupported] = useState(false)
  const [countdown, setCountdown] = useState(45)

  const countdownTimer = useRef<NodeJS.Timeout | null>(null)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  const [isTablePickerOpen, setIsTablePickerOpen] = useState(false)

  const handleTablePickerSuccess = useCallback(
    (assignment: { tableId: string; tableNumber: string } | null) => {
      if (!selectedGuest) return
      setSelectedGuest({
        ...selectedGuest,
        tableAssignment: assignment
          ? {
              tableId: assignment.tableId,
              tableNumber: assignment.tableNumber,
              zone: null,
            }
          : null,
      })
      setCountdown(45)
    },
    [selectedGuest],
  )

  // Fullscreen support check
  useEffect(() => {
    const supported = !!(
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled
    )
    setIsFullscreenSupported(supported)

    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!(document.fullscreenElement || document.webkitFullscreenElement),
      )
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      )
    }
  }, [])

  // Fullscreen toggle handler
  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      const element = document.documentElement
      const requestMethod =
        element.requestFullscreen ||
        element.webkitRequestFullscreen ||
        element.mozRequestFullScreen ||
        element.msRequestFullscreen

      if (requestMethod) {
        await requestMethod.call(element)
      }
    } else {
      const exitMethod =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen

      if (exitMethod) {
        await exitMethod.call(document)
      }
    }
  }, [])

  // Debounce search query
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 300)

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [searchQuery])

  // Inactivity timeout handler for selected guest view
  useEffect(() => {
    if (selectedGuest) {
      setCountdown(45)
      if (countdownTimer.current) clearInterval(countdownTimer.current)

      countdownTimer.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setSelectedGuest(null)
            setSearchQuery('')
            setIsTablePickerOpen(false)
            return 45
          }
          return prev - 1
        })
      }, 1000)
    } else {
      if (countdownTimer.current) {
        clearInterval(countdownTimer.current)
      }
    }

    return () => {
      if (countdownTimer.current) clearInterval(countdownTimer.current)
    }
  }, [selectedGuest])

  // Fetch guests based on debounced search
  const { data: guestsData, isFetching: isGuestsFetching } = useQuery({
    ...getApiGuestsOptions({
      query: {
        q: debouncedSearch || undefined,
        limit: 30,
        page: 1,
      },
    }),
    enabled: isLoggedIn && debouncedSearch.length >= 2,
  })

  const guestsList = (guestsData?.data ?? []) as GuestItem[]

  // Login handler
  const handleLogin = () => {
    liff.login({ redirectUri: window.location.href })
  }

  // Logout handler
  const handleLogout = () => {
    if (confirm('คุณต้องการออกจากระบบ Kiosk ใช่หรือไม่?')) {
      liff.logout()
      window.location.reload()
    }
  }

  if (!isReady) {
    return <FullPageSpinnerPortal />
  }

  const isSearching = searchQuery.trim().length >= 2

  return (
    <LazyMotion features={domAnimation}>
      <div className="text-forest-light relative flex min-h-screen flex-col items-center justify-start overflow-hidden bg-[#eb9fa5] font-sans">
        {/* Subtle grid pattern overlay for texture */}
        <div className="botanical-bg absolute inset-0 opacity-20" />
        <div className="floral-overlay opacity-[0.03]" />

        {/* Ambient Blur Orbs for background depth */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <m.div
            animate={{
              scale: [1, 1.15, 1],
              opacity: [0.3, 0.5, 0.3],
              x: [0, 20, 0],
              y: [0, 15, 0],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="bg-blush-soft absolute -top-32 -left-32 size-125 rounded-full blur-[100px]"
          />
          <m.div
            animate={{
              scale: [1, 1.25, 1],
              opacity: [0.15, 0.3, 0.15],
              x: [0, -20, 0],
              y: [0, 30, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2,
            }}
            className="bg-sage-light/30 absolute top-1/3 -right-32 size-150 rounded-full blur-[120px]"
          />
        </div>

        {/* Top Header Panel */}
        <div className="pointer-events-none absolute top-6 right-6 left-6 z-30 flex items-center justify-between">
          <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/40 px-4 py-1.5 shadow-sm backdrop-blur-md">
            <Sparkles className="text-rose-dark size-4" />
            <span className="text-forest-light/80 text-xs font-semibold tracking-wider uppercase">
              Table Finder Kiosk
            </span>
          </div>

          <div className="pointer-events-auto flex items-center gap-3">
            {isLoggedIn && (
              <button
                onClick={handleLogout}
                className="text-forest-light/70 flex size-10 items-center justify-center rounded-full border border-white/20 bg-white/40 backdrop-blur-md transition-all hover:bg-red-50 hover:text-red-600 active:scale-95"
                title="Staff Sign Out"
              >
                <LogOut className="size-4" />
              </button>
            )}

            {isFullscreenSupported && (
              <button
                onClick={toggleFullscreen}
                className="text-forest-light/70 flex size-10 items-center justify-center rounded-full border border-white/20 bg-white/40 backdrop-blur-md transition-all hover:bg-white/80 hover:text-[#344C3D] active:scale-95"
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? (
                  <Minimize2 className="size-4" />
                ) : (
                  <Maximize2 className="size-4" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="relative z-10 flex size-full min-h-screen max-w-4xl flex-col items-center justify-between px-6 pt-24 pb-8">
          <AnimatePresence mode="wait">
            {/* ─── CASE 1: NOT LOGGED IN (SETUP STATE) ─── */}
            {!isLoggedIn ? (
              <div className="flex w-full flex-1 items-center justify-center">
                <m.div
                  key="setup"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="wedding-card flex w-full max-w-lg flex-col items-center border-none bg-white/95 p-8 text-center shadow-2xl"
                >
                  <div className="bg-rose-dark/15 text-rose-dark mb-6 flex size-16 items-center justify-center rounded-2xl">
                    <Info className="text-rose-dark size-8 animate-pulse" />
                  </div>
                  <h2 className="text-forest-light text-2xl font-bold tracking-tight md:text-3xl">
                    Kiosk Staff Setup
                  </h2>
                  <p className="text-forest-light/60 mt-2 text-sm font-medium">
                    ระบบบริการค้นหาที่นั่งและลงทะเบียนเข้างาน (Pink Theme)
                  </p>

                  <div className="bg-forest-light/5 text-forest-light/70 my-6 w-full space-y-2 rounded-2xl p-4 text-left text-xs leading-relaxed">
                    <p className="text-forest-light font-semibold">
                      คำแนะนำสำหรับผู้จัดงาน (Staff Setup Guide):
                    </p>
                    <p>
                      1. กดปุ่มด้านล่างเพื่อเข้าสู่ระบบผ่านบัญชี LINE ของทีมงาน
                    </p>
                    <p>
                      2.
                      ระบบจะเชื่อมต่อฐานข้อมูลรายชื่อและผังโต๊ะนั่งของแขกทั้งหมด
                    </p>
                    <p>
                      3. แนะนำให้เปิดโหมด "เต็มหน้าจอ"
                      เพื่อแสดงผลบนแท็บเล็ตประจำเคาน์เตอร์
                    </p>
                  </div>

                  <Button
                    onClick={handleLogin}
                    className="wedding-button bg-rose-dark shadow-rose-dark/25 hover:bg-rose-dark/90 h-12 w-full cursor-pointer border-none text-base font-bold text-white shadow-lg"
                  >
                    เปิดระบบค้นหาที่นั่ง (Staff Login)
                  </Button>
                </m.div>
              </div>
            ) : selectedGuest ? (
              /* ─── CASE 2: GUEST SELECTION DETAIL SCREEN ─── */
              <div className="flex w-full flex-1 items-center justify-center">
                <m.div
                  key="detail"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  className="wedding-card flex w-full max-w-2xl flex-col items-center border-none bg-white/95 p-8 shadow-2xl md:p-12"
                >
                  {/* Back Button */}
                  <button
                    onClick={() => {
                      setSelectedGuest(null)
                      setSearchQuery('')
                      setIsTablePickerOpen(false)
                    }}
                    className="text-forest-light/60 hover:text-forest-light mb-6 flex cursor-pointer items-center gap-1.5 self-start text-sm font-bold transition-colors"
                  >
                    <ChevronLeft className="size-4" />
                    กลับไปหน้าค้นหา (Back)
                  </button>

                  {/* Profile Header */}
                  <div className="w-full space-y-1 text-center">
                    <h3 className="text-rose-dark text-xs font-bold tracking-[0.2em] uppercase">
                      Welcome to our wedding
                    </h3>
                    <h2 className="text-forest-light text-3xl leading-tight font-extrabold break-all">
                      คุณ{selectedGuest.fullName}
                    </h2>
                    {selectedGuest.displayName && (
                      <p className="text-forest-light/50 text-sm font-semibold">
                        ({selectedGuest.displayName})
                      </p>
                    )}
                    {selectedGuest.group && (
                      <span className="bg-rose-dark/10 text-rose-dark mt-2 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold">
                        <Users className="size-3" />
                        {selectedGuest.group}
                      </span>
                    )}
                  </div>

                  {/* Seating Card */}
                  <div className="border-rose-dark/20 from-blush-soft/40 to-rose-dark/20 my-8 flex w-full max-w-md flex-col items-center justify-center rounded-[24px] border bg-linear-to-br p-6 text-center shadow-inner">
                    <Armchair className="text-rose-dark mb-2 size-10" />
                    <span className="text-forest-light/50 text-[10px] font-bold tracking-widest uppercase">
                      Your Dinner Seating
                    </span>
                    <div className="text-forest-light mt-1 font-sans text-4xl font-bold tracking-tight">
                      {selectedGuest.tableAssignment ? (
                        `โต๊ะที่ ${selectedGuest.tableAssignment.tableNumber}`
                      ) : (
                        <span className="text-forest-light/50 text-xl font-bold">
                          กำลังจัดเตรียมที่นั่ง (Pending)
                        </span>
                      )}
                    </div>
                    {selectedGuest.tableAssignment?.zone && (
                      <p className="text-rose-dark mt-1 text-sm font-bold">
                        ({selectedGuest.tableAssignment.zone})
                      </p>
                    )}
                  </div>

                  {/* Checkin QR Code Section */}
                  <div className="group relative flex flex-col items-center">
                    <div className="bg-rose-dark/25 absolute -inset-3 rounded-[36px] opacity-70 blur-xl" />
                    <div className="border-rose-dark/10 relative rounded-[28px] border bg-white p-6 shadow-xl">
                      <LIFFQR
                        path={`/checkin?checkinId=${selectedGuest.id}`}
                        size={200}
                        color="#344C3D"
                      />

                      {/* Corner Accents */}
                      <div className="border-rose-dark absolute -top-1 -left-1 size-6 rounded-tl-2xl border-t-4 border-l-4" />
                      <div className="border-rose-dark absolute -top-1 -right-1 size-6 rounded-tr-2xl border-t-4 border-r-4" />
                      <div className="border-rose-dark absolute -bottom-1 -left-1 size-6 rounded-bl-2xl border-b-4 border-l-4" />
                      <div className="border-rose-dark absolute -right-1 -bottom-1 size-6 rounded-br-2xl border-r-4 border-b-4" />
                    </div>
                  </div>

                  <div className="text-forest-light/80 mt-6 flex max-w-sm flex-row items-center gap-3 rounded-2xl border border-[#eb9fa5]/20 bg-[#eb9fa5]/10 p-4 text-left text-xs leading-relaxed">
                    <Smartphone className="text-rose-dark size-6 shrink-0" />
                    <p>
                      <strong>สแกนเพื่อเช็กอิน:</strong> กรุณาสแกน QR Code
                      นี้เพื่อเช็กอินเข้าร่วมงาน อวยพร
                      และรับของที่ระลึกที่จุดลงทะเบียน
                    </p>
                  </div>

                  {/* Edit Table — staff action */}
                  <button
                    onClick={() => setIsTablePickerOpen(true)}
                    className="text-forest-light/70 mt-4 flex items-center gap-2 rounded-full border border-white/30 bg-white/40 px-5 py-2.5 text-xs font-bold backdrop-blur-sm transition-all hover:bg-white/70 active:scale-95"
                  >
                    <Pencil className="size-3.5" />
                    เปลี่ยนโต๊ะที่นั่ง
                  </button>

                  {/* Auto Reset Timer */}
                  <div className="text-forest-light/40 mt-8 text-xs font-bold tracking-wide">
                    หน้าจอจะรีเซ็ตอัตโนมัติในอีก{' '}
                    <span className="text-rose-dark font-extrabold">
                      {countdown}
                    </span>{' '}
                    วินาที
                  </div>
                </m.div>
              </div>
            ) : (
              /* ─── CASE 3: INTERACTIVE GUEST SEARCH SCREEN ─── */
              <div className="flex w-full flex-1 flex-col items-center justify-center">
                <m.div
                  key="search"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex w-full flex-col items-center space-y-6 text-center"
                >
                  <AnimatePresence mode="wait">
                    {!isSearching && (
                      /* Top Visual Section with logo/cover (ONLY when not searching) */
                      <m.div
                        key="welcome-header"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{
                          opacity: 0,
                          y: -20,
                          transition: { duration: 0.3 },
                        }}
                        className="flex flex-col items-center space-y-4"
                      >
                        {/* Outline Logo */}
                        <div className="flex size-28 items-center justify-center overflow-hidden">
                          <img
                            src={logoUrl}
                            alt="Wedding Logo"
                            className="size-full object-contain brightness-0 invert"
                          />
                        </div>

                        {/* Cormorant Garamond Serif Welcome */}
                        <div className="space-y-1">
                          <h2
                            className="text-5xl tracking-[0.15em] text-white"
                            style={{
                              fontFamily: "'Cormorant Garamond', serif",
                              fontWeight: 400,
                            }}
                          >
                            WELCOME
                          </h2>
                          <p className="text-[10px] font-extrabold tracking-[0.25em] text-[#344C3D]/80 uppercase">
                            TECH WEDDING MEETUP
                          </p>
                        </div>

                        <p className="text-xs font-semibold tracking-wide text-white/90">
                          ค้นหาโต๊ะอาหารและสแกนคิวอาร์โค้ดลงทะเบียน
                        </p>
                      </m.div>
                    )}
                  </AnimatePresence>

                  {/* Search Input Widget */}
                  <m.div
                    layout
                    className="relative w-full max-w-xl transition-all duration-300"
                  >
                    <div className="text-forest-light/50 absolute top-1/2 left-4 -translate-y-1/2">
                      <Search className="size-5" />
                    </div>
                    <Input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="พิมพ์ชื่อจริง หรือชื่อเล่นของท่านเพื่อค้นหา..."
                      className="text-forest-light placeholder:text-forest-light/40 focus-visible:ring-rose-dark/40 h-14 w-full rounded-full border-none bg-white/95 pr-12 pl-12 text-base font-bold shadow-xl focus-visible:ring-3 focus-visible:ring-offset-0"
                      autoFocus
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="bg-forest-light/10 text-forest-light/60 hover:bg-forest-light/20 absolute top-1/2 right-4 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full transition-all"
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </m.div>

                  {/* Dynamic Search Results */}
                  <div className="flex min-h-60 w-full max-w-2xl flex-col justify-start">
                    <AnimatePresence mode="wait">
                      {!isSearching ? (
                        <></>
                      ) : isGuestsFetching ? (
                        /* Skeletons */
                        <m.div
                          key="loading"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="w-full space-y-3"
                        >
                          {[...Array(3)].map((_, i) => (
                            <div
                              key={i}
                              className="wedding-card flex items-center justify-between border-none bg-white/90 p-5 shadow-lg"
                            >
                              <div className="space-y-2 text-left">
                                <Skeleton className="bg-forest-light/15 h-5 w-44 rounded-full" />
                                <Skeleton className="bg-forest-light/10 h-4.5 w-32 rounded-full" />
                              </div>
                              <Skeleton className="bg-forest-light/15 h-9 w-20 rounded-full" />
                            </div>
                          ))}
                        </m.div>
                      ) : guestsList.length === 0 ? (
                        /* Empty state */
                        <m.div
                          key="no-results"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          className="wedding-card flex flex-col items-center justify-center space-y-2 border-none bg-white/90 p-8 text-center shadow-xl"
                        >
                          <span className="text-forest-light text-base font-bold">
                            ไม่พบข้อมูลรายชื่อของคุณ
                          </span>
                          <p className="text-forest-light/60 max-w-sm text-xs font-medium">
                            กรุณาตรวจสอบการสะกดชื่ออีกครั้ง
                            หรือติดต่อเจ้าหน้าที่ที่โต๊ะต้อนรับเพื่อช่วยอำนวยความสะดวก
                          </p>
                        </m.div>
                      ) : (
                        /* Results list with giant touch targets for tablet */
                        <m.div
                          key="list"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="max-h-[45vh] w-full space-y-3 overflow-y-auto pr-1 pb-4"
                        >
                          {guestsList.map((guest, idx) => (
                            <m.button
                              key={guest.id}
                              initial={{ opacity: 0, y: 15 }}
                              animate={{
                                opacity: 1,
                                y: 0,
                                transition: { delay: idx * 0.05 },
                              }}
                              onClick={() => setSelectedGuest(guest)}
                              className="wedding-card group flex w-full cursor-pointer items-center justify-between border-none bg-white/95 p-5 text-left shadow-lg transition-all duration-200 hover:bg-white active:scale-[0.99]"
                            >
                              <div className="space-y-1 pr-4">
                                <span className="text-forest-light group-hover:text-rose-dark text-lg leading-tight font-bold transition-colors">
                                  คุณ{guest.fullName}
                                </span>
                                <div className="text-forest-light/50 mt-0.5 flex flex-wrap items-center gap-2 text-xs font-bold">
                                  {guest.displayName && (
                                    <span>ชื่อเล่น: {guest.displayName}</span>
                                  )}
                                  {guest.displayName && guest.group && (
                                    <span className="text-forest-light/20">
                                      •
                                    </span>
                                  )}
                                  {guest.group && <span>{guest.group}</span>}
                                </div>
                              </div>

                              <div className="flex shrink-0 items-center gap-2">
                                {guest.tableAssignment ? (
                                  <span className="bg-rose-dark inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-extrabold text-white shadow-md">
                                    โต๊ะ {guest.tableAssignment.tableNumber}
                                  </span>
                                ) : (
                                  <span className="bg-forest-light/10 text-forest-light/60 inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-bold">
                                    รอจัดโต๊ะ
                                  </span>
                                )}
                              </div>
                            </m.button>
                          ))}
                        </m.div>
                      )}
                    </AnimatePresence>
                  </div>
                </m.div>
              </div>
            )}
          </AnimatePresence>

          {/* Table picker overlay */}
          {selectedGuest && (
            <KioskTablePicker
              isOpen={isTablePickerOpen}
              onClose={() => setIsTablePickerOpen(false)}
              guest={{
                id: selectedGuest.id,
                fullName: selectedGuest.fullName,
                currentTableId: selectedGuest.tableAssignment?.tableId,
                tableNumber: selectedGuest.tableAssignment?.tableNumber,
              }}
              onSuccess={handleTablePickerSuccess}
            />
          )}

          {/* Simple signature footer */}
          <div className="text-[10px] font-semibold tracking-wider text-white/50 select-none">
            OA & KOI WEDDING CELEBRATION
          </div>
        </div>
      </div>
    </LazyMotion>
  )
}
